/** Máximo de chaves distintas por colagem (RCP-20): protege a API de uma colagem acidental enorme. */
export const LIMITE_DA_COLAGEM = 300;

/** Consultas simultâneas ao `findPlayer` (RCP-13). */
const CONSULTAS_SIMULTANEAS = 5;

// `F` + 7 dígitos, sem letra ou dígito colado antes ou depois (`F12345678` e `XF1234567` não são chaves). O separador
// de antes é capturado em vez de usar lookbehind, que o Safari anterior ao 16.4 não entende.
const CHAVE = /(^|[^A-Za-z0-9])(F\d{7})(?![A-Za-z0-9])/gi;

/** Chaves de funcionário coladas num texto qualquer, em maiúsculas, sem repetição e na ordem em que aparecem (RCP-11). */
export function extrairChaves(texto: string): string[] {
  const chaves = Array.from(texto.matchAll(CHAVE), ([, , chave]) => chave.toUpperCase());
  return Array.from(new Set(chaves));
}

export type ResultadoDaConsulta<T> = { chave: string; pessoas: T } | { chave: string; erro: unknown };

/**
 * Consulta cada chave com no máximo 5 consultas em andamento. Devolve um resultado por chave, na ordem de entrada; a
 * falha de uma chave vira `erro` no resultado dela e não interrompe as outras (RCP-18).
 */
export async function consultarEmLotes<T>(
  chaves: string[],
  consultar: (chave: string) => Promise<T>,
  { onProgresso }: { onProgresso?: (feitas: number, total: number) => void } = {},
): Promise<ResultadoDaConsulta<T>[]> {
  const resultados: ResultadoDaConsulta<T>[] = new Array(chaves.length);
  let proxima = 0;
  let feitas = 0;

  // O total anunciado é o das chaves que serão consultadas, desde o primeiro aviso (RCP-13).
  onProgresso?.(0, chaves.length);

  async function trabalhar() {
    while (proxima < chaves.length) {
      const indice = proxima++;
      const chave = chaves[indice];

      try {
        resultados[indice] = { chave, pessoas: await consultar(chave) };
      } catch (erro) {
        resultados[indice] = { chave, erro };
      }

      feitas += 1;
      onProgresso?.(feitas, chaves.length);
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONSULTAS_SIMULTANEAS, chaves.length) }, trabalhar));
  return resultados;
}
